import type { DeviceExtensions, DetectionMode } from '../../schema/device.js';
import type { FeatureFlag } from '../../schema/features.js';

import type { DeviceExtensionModule, OemId, OemModuleLoader } from './types.js';

/**
 * Structured events emitted by the orchestrator. The engine pipes these to
 * telemetry when entitled (Phase 4) and to the diagnostic overlay. Each
 * event is one observable thing happening to OEM loading; consumers can
 * ignore unfamiliar types.
 */
export type OemEvent =
  | { type: 'applied'; oem: OemId }
  | { type: 'skippedNotDetected'; oem: OemId }
  | { type: 'skippedNotEntitled'; oem: OemId }
  | { type: 'skippedDetectionDisabled' }
  | { type: 'loadFailed'; oem: OemId; error: Error }
  | { type: 'detectFailed'; oem: OemId; error: Error }
  | { type: 'applyFailed'; oem: OemId; error: Error };

export interface OemOrchestratorStartInput {
  features: ReadonlySet<FeatureFlag>;
  device: DeviceExtensions | undefined;
  loadModule: OemModuleLoader;
  onEvent?: (event: OemEvent) => void;
}

/** Canonical OEM order used when iterating entitlements (zebra → samsung → honeywell). */
const OEM_ORDER: readonly OemId[] = ['zebra', 'samsung', 'honeywell'];

const FORCE_TO_OEM: Readonly<Partial<Record<DetectionMode, OemId>>> = {
  forceZebra: 'zebra',
  forceSamsung: 'samsung',
  forceHoneywell: 'honeywell',
};

/**
 * OEM loader / detector / applier. At most one OEM module activates per
 * device — a Zebra TC58 isn't simultaneously a Samsung XCover.
 *
 * Failure isolation: any throw or rejection during load, detect, or apply
 * is caught and surfaced as an event; the engine continues without OEM
 * features. This is the "fail to Core" principle for hardware extensions.
 */
export class OemOrchestrator {
  private active: DeviceExtensionModule | null = null;

  /** Whichever OEM (if any) is currently activated on the device. */
  get activeOem(): OemId | null {
    return this.active?.oem ?? null;
  }

  async start(input: OemOrchestratorStartInput): Promise<void> {
    const detection: DetectionMode = input.device?.detection ?? 'auto';
    if (detection === 'none') {
      input.onEvent?.({ type: 'skippedDetectionDisabled' });
      return;
    }

    const entitled = OEM_ORDER.filter((o) => input.features.has(`oem.${o}`));
    if (entitled.length === 0) return;

    const forced = FORCE_TO_OEM[detection];
    const candidates = forced ? [forced] : entitled;

    for (const oem of candidates) {
      if (!entitled.includes(oem)) {
        input.onEvent?.({ type: 'skippedNotEntitled', oem });
        continue;
      }

      const module = await this.tryLoad(oem, input);
      if (!module) continue;

      const detected = await this.tryDetect(oem, module, forced !== undefined, input);
      if (detected === null) continue; // detect threw — try next OEM
      if (!detected) {
        input.onEvent?.({ type: 'skippedNotDetected', oem });
        continue;
      }

      // Detect succeeded — commit. If apply fails, no OEM activates; we do
      // NOT fall through to the next OEM (a Zebra TC58 isn't simultaneously
      // a Samsung XCover, so it doesn't make sense to try Samsung after
      // Zebra confirmed it's the hardware).
      const applied = await this.tryApply(oem, module, input);
      if (applied) {
        this.active = module;
        input.onEvent?.({ type: 'applied', oem });
      }
      return;
    }
  }

  async dispose(): Promise<void> {
    if (!this.active) return;
    const m = this.active;
    this.active = null;
    try {
      await m.dispose();
    } catch {
      // Best-effort dispose; nothing the launcher can do if teardown fails.
    }
  }

  private async tryLoad(
    oem: OemId,
    input: OemOrchestratorStartInput,
  ): Promise<DeviceExtensionModule | null> {
    try {
      return await input.loadModule(oem);
    } catch (err) {
      input.onEvent?.({ type: 'loadFailed', oem, error: asError(err) });
      return null;
    }
  }

  private async tryDetect(
    oem: OemId,
    module: DeviceExtensionModule,
    forced: boolean,
    input: OemOrchestratorStartInput,
  ): Promise<boolean | null> {
    if (forced) return true;
    try {
      return await module.detect();
    } catch (err) {
      input.onEvent?.({ type: 'detectFailed', oem, error: asError(err) });
      return null;
    }
  }

  private async tryApply(
    oem: OemId,
    module: DeviceExtensionModule,
    input: OemOrchestratorStartInput,
  ): Promise<boolean> {
    const block = input.device ? input.device[oem] : undefined;
    try {
      await module.apply(block);
      return true;
    } catch (err) {
      input.onEvent?.({ type: 'applyFailed', oem, error: asError(err) });
      // Tear down the partially-applied module so it doesn't leak state.
      try {
        await module.dispose();
      } catch {
        // Best effort.
      }
      return false;
    }
  }
}

function asError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}
